// ข้อความเลือกจาก `code` (dict.errors) — `detail` ดิบไม่โชว์ผู้ใช้ ใส่ไว้ในปุ่มคัดลอกส่ง IT แทน

import React from 'react'
import PropTypes from 'prop-types'
import { Icon, message } from 'antd'
import { CELL } from '../lib/constants'
import { isOfflineCode } from '../lib/cellState'
import useElapsedSeconds from '../lib/useElapsedSeconds'
import { fill } from '../dict'
import {
  BadgeAction,
  BadgeHint,
  StatusAction,
  StatusActions,
  StatusBadge,
  StatusBox,
  StatusHint,
  StatusText,
} from '../styles/cell'

const TONE = {
  info: '#d9d9d9',
  warn: '#faad14',
  error: '#ff7875',
}

/** บังทั้งกรอบเฉพาะสถานะที่ยืนยันแล้วว่าไม่มีอะไรให้ดู */
function coversWholeCell(state) {
  return (
    state === CELL.EMPTY ||
    state === CELL.ERROR ||
    state === CELL.OFFLINE ||
    state === CELL.STOPPED
  )
}

/** ปุ่มในกล่องสถานะไม่ควรไปสั่ง "เลือกกรอบ" ที่ผูกไว้กับตัว Cell ด้วย */
const stopAnd = handler => event => {
  event.stopPropagation()
  handler()
}

function errorDetail(error, txt) {
  const code = (error && error.code) || 'error'
  const isOffline = isOfflineCode(error)
  return {
    tone: isOffline ? TONE.warn : TONE.error,
    text: txt.errors[code] || code,
    hint: txt.errorHints[code] || null,
    icon: isOffline ? 'api' : 'close-circle',
  }
}

function describe({ state, error, endReason, elapsedSec, txt }) {
  switch (state) {
    case CELL.EMPTY:
      return { tone: TONE.info, text: txt.emptyCell, icon: 'select' }
    case CELL.STARTING:
      return { tone: TONE.info, text: txt.starting, icon: 'loading' }
    case CELL.CONNECTING:
      return {
        tone: TONE.info,
        text: elapsedSec
          ? fill(txt.connectingFor, { sec: elapsedSec })
          : txt.connecting,
        icon: 'loading',
      }
    case CELL.STALLED:
      return { tone: TONE.warn, text: txt.stalled, icon: 'pause-circle' }
    case CELL.STOPPED:
      return {
        tone: TONE.info,
        text: txt.stopped,
        hint: endReason ? txt.endReason[endReason] || endReason : null,
        icon: 'stop',
      }
    case CELL.UNKNOWN:
      return {
        tone: TONE.info,
        text: txt.unknownState,
        hint: txt.unknownHint,
        icon: 'question-circle',
      }
    case CELL.OFFLINE:
      return error
        ? errorDetail(error, txt)
        : { tone: TONE.warn, text: txt.offline, icon: 'api' }
    case CELL.ERROR:
      return errorDetail(error, txt)
    default:
      return null
  }
}

function supportText({ error, sessionId, vehicleId }) {
  const lines = [
    `code: ${error.code}`,
    error.detail && `detail: ${error.detail}`,
    error.requestId && `request_id: ${error.requestId}`,
    sessionId && `session_id: ${sessionId}`,
    vehicleId && `vehicle_id: ${vehicleId}`,
    `at: ${new Date().toISOString()}`,
  ]
  return lines.filter(Boolean).join('\n')
}

function copyText(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text)
  }
  return new Promise((resolve, reject) => {
    const el = document.createElement('textarea')
    el.value = text
    el.setAttribute('readonly', '')
    el.style.position = 'fixed'
    el.style.opacity = '0'
    document.body.appendChild(el)
    el.select()
    const ok = document.execCommand && document.execCommand('copy')
    document.body.removeChild(el)
    ok ? resolve() : reject(new Error('copy failed'))
  })
}

export default function CellStatus({
  state,
  error,
  endReason,
  waitingSince,
  sessionId,
  vehicleId,
  txt,
  onRetry,
}) {
  const elapsedSec = useElapsedSeconds(
    state === CELL.CONNECTING ? waitingSince : null,
  )

  if (state === CELL.PLAYING) return null

  const info = describe({ state, error, endReason, elapsedSec, txt })
  if (!info) return null

  const busy = state === CELL.STARTING || state === CELL.CONNECTING
  const retryable = !error || error.retryable !== false
  const canRetry = !busy && state !== CELL.EMPTY && retryable && !!onRetry
  // เบราว์เซอร์ไม่ยอมเล่นอัตโนมัติ — ไม่ใช่ปัญหาที่ต้องส่ง IT แค่ให้ผู้ใช้กดเล่น
  const tapToPlay = !!error && error.code === 'autoplay_blocked'
  const canCopy =
    !!error && !tapToPlay && (state === CELL.ERROR || state === CELL.OFFLINE)
  const retryLabel = tapToPlay ? txt.tapToPlay : txt.retry

  const onCopy = () =>
    copyText(supportText({ error, sessionId, vehicleId }))
      .then(() => message.success(txt.copied, 1.5))
      .catch(() => {})

  // ป้ายมุมสำหรับสถานะที่ "อาจมีภาพอยู่แล้ว" — ห้ามเอากล่องทับเต็มกรอบ ภาพที่เล่นอยู่ต้องเห็นได้
  if (!coversWholeCell(state)) {
    return (
      <StatusBadge>
        <Icon type={info.icon} spin={info.icon === 'loading'} />
        <span>{info.text}</span>
        {info.hint && (
          <BadgeHint title={info.hint}>
            <Icon type="question-circle" />
          </BadgeHint>
        )}
        {canRetry && (
          <BadgeAction type="button" onClick={stopAnd(onRetry)}>
            {txt.retry}
          </BadgeAction>
        )}
      </StatusBadge>
    )
  }

  return (
    <StatusBox>
      <Icon type={info.icon} spin={info.icon === 'loading'} />
      <StatusText tone={info.tone}>{info.text}</StatusText>
      {info.hint && <StatusHint>{info.hint}</StatusHint>}
      {(canRetry || canCopy) && (
        <StatusActions>
          {canRetry && (
            <StatusAction type="button" onClick={stopAnd(onRetry)}>
              {retryLabel}
            </StatusAction>
          )}
          {canCopy && (
            <StatusAction type="button" onClick={stopAnd(onCopy)}>
              {txt.copyDetail}
            </StatusAction>
          )}
        </StatusActions>
      )}
    </StatusBox>
  )
}

CellStatus.propTypes = {
  state: PropTypes.string.isRequired,
  /** { code, detail, requestId, retryable } */
  error: PropTypes.object,
  endReason: PropTypes.string,
  /** Date.now() ตอนกรอบเริ่มรอภาพ */
  waitingSince: PropTypes.number,
  sessionId: PropTypes.string,
  vehicleId: PropTypes.number,
  txt: PropTypes.object.isRequired,
  onRetry: PropTypes.func,
}
